import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowRight, Phone, Globe, LinkedinLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { buildVCard, downloadVCard } from "../lib/vcard";

export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", title: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [refForm, setRefForm] = useState({ from_name: "", from_email: "", referral_name: "", referral_email: "", referral_phone: "", referral_company: "", note: "" });
  const [refBusy, setRefBusy] = useState(false);
  const [showRefer, setShowRefer] = useState(false);

  useEffect(() => {
    api.get(`/cards/public-ext/${slug}`).then((r) => setCard(r.data)).catch(() => setCard({ notfound: true }));
  }, [slug]);

  if (!card) return <div className="min-h-screen flex items-center justify-center text-[#C7DDE5]">Loading...</div>;
  if (card.notfound) return <div className="min-h-screen flex items-center justify-center text-[#C7DDE5]">Card not found</div>;

  const submitInfo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/cards/save-contact/${slug}`, form);
      toast.success("Sent! They'll be in touch.");
      setShowForm(false);
      setForm({ name: "", email: "", phone: "", company: "", title: "", notes: "" });
    } catch { toast.error("Failed to send"); }
    finally { setBusy(false); }
  };

  const vCard = buildVCard({
    name: card.name, email: card.email, phone: card.phone, title: card.title,
    company: card.company, website: card.website, linkedin: card.linkedin,
    tagline: card.tagline,
  });
  const saveVcf = () => downloadVCard(vCard, `${(card.name || "contact").replace(/\s+/g, "_")}.vcf`);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-10" style={{ background: "var(--bg)" }} data-testid="public-card-page">
      <Toaster theme="dark" />
      <div className="w-full max-w-2xl">
        <div className="border p-6 sm:p-8 md:p-12 grid-bg relative overflow-hidden" style={{ borderColor: "var(--border)" }}>
          <div className="overline mb-6 truncate">// {card.company || "independent operator"}</div>
          <h1
            className="font-display font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05]"
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
            data-testid="card-name"
          >
            {card.name}
          </h1>
          {card.title && <div className="font-mono text-sm mt-4 text-[#C7DDE5] uppercase tracking-wider break-words">{card.title}</div>}
          {card.tagline && <div className="mt-6 text-base md:text-lg max-w-full md:max-w-md break-words">{card.tagline}</div>}

          <div className="mt-10 space-y-3">
            {card.phone && <a href={`tel:${card.phone}`} className="flex items-center gap-3 hover:text-white text-[#C7DDE5] break-all min-w-0" data-testid="card-phone"><Phone weight="bold" className="shrink-0" /> <span className="break-all">{card.phone}</span></a>}
            {card.email && <a href={`mailto:${card.email}`} className="flex items-center gap-3 hover:text-white text-[#C7DDE5] break-all min-w-0" data-testid="card-email"><EnvelopeSimple weight="bold" className="shrink-0" /> <span className="break-all">{card.email}</span></a>}
            {card.website && <a href={card.website.startsWith("http") ? card.website : `https://${card.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-white text-[#C7DDE5] break-all min-w-0" data-testid="card-website"><Globe weight="bold" className="shrink-0" /> <span className="break-all">{card.website}</span></a>}
            {card.linkedin && <a href={card.linkedin.startsWith("http") ? card.linkedin : `https://${card.linkedin}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-white text-[#C7DDE5]" data-testid="card-linkedin"><LinkedinLogo weight="bold" className="shrink-0" /> LinkedIn</a>}
          </div>

          {card.bio && <p className="mt-8 text-[#C7DDE5] leading-relaxed break-words">{card.bio}</p>}

          {card.calendly_url && (
            <div className="mt-8">
              <a href={card.calendly_url} target="_blank" rel="noreferrer" data-testid="card-book-btn"
                className="btn-primary w-full flex items-center justify-between text-lg py-4"
                style={{ boxShadow: "0 0 24px rgba(45,212,212,0.2)" }}>
                {card.booking_cta || "Book a meeting with me"} <ArrowRight weight="bold" />
              </a>
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            <button onClick={saveVcf} data-testid="save-vcf-btn" className="btn-secondary border-t-0 border-l-0 flex items-center justify-center gap-2 py-4">
              Save to contacts <ArrowRight weight="bold" />
            </button>
            <button onClick={() => setShowForm(!showForm)} data-testid="exchange-info-btn" className="btn-primary flex items-center justify-center gap-2 py-4" style={{ boxShadow: "none" }}>
              Send your info back <ArrowRight weight="bold" />
            </button>
          </div>

          {showForm && (
            <form onSubmit={submitInfo} className="mt-6 border p-5" style={{ borderColor: "var(--border)" }} data-testid="exchange-form">
              <div className="overline mb-3">// share back</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[["name", "Name *", true], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["title", "Title"]].map(([k, l, req]) => (
                  <div key={k}>
                    <label className="label-base">{l}</label>
                    <input required={!!req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="input-base" data-testid={`exch-${k}`} />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="label-base">Notes</label>
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-base" data-testid="exch-notes" placeholder="Where you met / topic discussed" />
                </div>
              </div>
              <button type="submit" disabled={busy} className="btn-primary mt-4 w-full" data-testid="exch-submit">{busy ? "Sending..." : "Send"}</button>
              {card.referral_enabled && !showRefer && (
                <button type="button" onClick={() => setShowRefer(true)} className="btn-secondary mt-3 w-full" data-testid="show-refer-btn">+ Refer someone too</button>
              )}
            </form>
          )}

          {card.referral_enabled && showRefer && (
            <form onSubmit={async (e) => {
              e.preventDefault(); setRefBusy(true);
              try { await api.post(`/cards/refer/${slug}`, refForm); toast.success("Referral sent. Thank you!"); setShowRefer(false); setRefForm({ from_name: "", from_email: "", referral_name: "", referral_email: "", referral_phone: "", referral_company: "", note: "" }); }
              catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
              finally { setRefBusy(false); }
            }} className="mt-6 border p-5" style={{ borderColor: "var(--accent)" }} data-testid="refer-form">
              <div className="overline mb-2" style={{ color: "var(--accent)" }}>// refer someone</div>
              <p className="text-sm text-[#C7DDE5] mb-4">{card.referral_prompt}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="label-base">Your name *</label><input required value={refForm.from_name} onChange={(e) => setRefForm({ ...refForm, from_name: e.target.value })} className="input-base" data-testid="ref-fromname" /></div>
                <div><label className="label-base">Your email</label><input value={refForm.from_email} onChange={(e) => setRefForm({ ...refForm, from_email: e.target.value })} className="input-base" data-testid="ref-fromemail" /></div>
                <div><label className="label-base">Their name *</label><input required value={refForm.referral_name} onChange={(e) => setRefForm({ ...refForm, referral_name: e.target.value })} className="input-base" data-testid="ref-name" /></div>
                <div><label className="label-base">Their email</label><input value={refForm.referral_email} onChange={(e) => setRefForm({ ...refForm, referral_email: e.target.value })} className="input-base" data-testid="ref-email" /></div>
                <div><label className="label-base">Their phone</label><input value={refForm.referral_phone} onChange={(e) => setRefForm({ ...refForm, referral_phone: e.target.value })} className="input-base" data-testid="ref-phone" /></div>
                <div><label className="label-base">Their company</label><input value={refForm.referral_company} onChange={(e) => setRefForm({ ...refForm, referral_company: e.target.value })} className="input-base" data-testid="ref-company" /></div>
                <div className="md:col-span-2"><label className="label-base">Why they would love to connect</label><input value={refForm.note} onChange={(e) => setRefForm({ ...refForm, note: e.target.value })} className="input-base" data-testid="ref-note" /></div>
              </div>
              <button type="submit" disabled={refBusy} className="btn-primary mt-4 w-full" data-testid="ref-submit">{refBusy ? "Sending..." : "Send referral"}</button>
            </form>
          )}
        </div>
        <div className="mt-6 text-center">
          <Link to="/" className="overline hover:text-white">powered by MY NEXUS CARD →</Link>
        </div>
      </div>
    </div>
  );
}
